import * as React from 'react';
import Box from '@mui/material/Box';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarProps,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Button,
  CircularProgress,
  debounce,
  LinearProgress,
} from '@mui/material';
import HeaderElipse from './headerElipse';
import RowButton from './rowButton';
import DropDown from './dropDown';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useDispatch, useSelector } from '../../state';
import {
  setSearchInput,
  searchItems,
  setLoading,
} from '../../state/itemBrowseSlice';
import type { ItemSearchInput } from '@repo/api-contract';
import { topNavBarHeight } from '../NavBars/constants';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { openNewItemModal } from '../../state/itemModalSlice';
import {
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../util/hooks';
import type { DataGridTableId } from '../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'browse-items' satisfies DataGridTableId;

const ITEM_TABLE_COL_I18N = 'team.Components.ItemBrowseTable';
const GLOBAL_COMMON_COL_I18N = 'team.Global.Common';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar(
  props: GridToolbarProps & { selectedRows?: GridRowSelectionModel },
) {
  const { selectedRows = [] } = props;
  const dispatch = useDispatch();
  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { t: tCV } = useTranslation([], {
    keyPrefix: 'team.Components.ItemBrowseTable',
  });
  const { searchInput, searchBy, loading } = useSelector(
    (store) => store.itemBrowseSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: ItemSearchInput = {
        query: searchQuery,
        searchBy,
        size: 100,
        page: 0,
      };
      dispatch(searchItems(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <DropDown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={tCV('Search for items...')}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
            </React.Fragment>
          ),
        }}
      />
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          const payload: ItemSearchInput = {
            query: searchInput,
            searchBy,
            size: 100,
            page: 0,
          };
          dispatch(searchItems(payload));
        }}
      >
        {tGB('Search')}
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <button
        className="text-blue-600 border-0 bg-inherit hover:cursor-pointer"
        onClick={() => {
          dispatch(openNewItemModal());
        }}
      >
        {tGB('+ ADD')}
      </button>
      <GridToolbarExport
        slotProps={{
          tooltip: { title: 'Export data' },
        }}
      />
      <HeaderElipse selectedRows={selectedRows} />
    </GridToolbarContainer>
  );
}

export default function DataTables() {
  const dispatch = useDispatch();
  const { results, loading, searchBy } = useSelector(
    (store) => store.itemBrowseSlice,
  );
  const { team } = useSelector((store) => store.userSlice);
  const apiRef = useGridApiRef();
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);

  const initialResults = async () => {
    if (team === 'init' || team === 'loading') return null;
    const payload: ItemSearchInput = {
      query: '',
      searchBy: 'vendorItemNumber',
      size: 100,
      page: 0,
    };
    await dispatch(searchItems(payload));
  };

  React.useEffect(() => {
    initialResults();
  }, []);

  const baseColumns = React.useMemo((): GridColDef[] => {
    return [
      {
        field: 'customerItemNumber',
        type: 'string',
        width: 160,
        headerName: 'Customer Item Id',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Item Id"
            params={params}
            keyPrefix={ITEM_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'vendorItemNumber',
        type: 'string',
        width: 160,
        headerName: 'Vendor Item Number',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Item Number"
            params={params}
            keyPrefix={ITEM_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'description',
        type: 'string',
        width: 160,
        headerName: 'Description',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Description"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'customerPrice',
        width: 140,
        headerName: 'Customer Price',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Price"
            params={params}
            keyPrefix={ITEM_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'unit',
        width: 110,
        headerName: 'Unit',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Unit"
            params={params}
            keyPrefix={ITEM_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'commissionable',
        width: 130,
        headerName: 'Comissionable',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Comissionable"
            params={params}
            keyPrefix={ITEM_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{JSON.stringify(params.value)}</span>
        ),
      },
      {
        field: 'assemblyItem',
        type: 'string',
        width: 130,
        headerName: 'Assembly Item',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Assembly Item"
            params={params}
            keyPrefix={ITEM_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{JSON.stringify(params.value)}</span>
        ),
      },
      {
        field: 'minimumRequirements',
        sortable: false,
        width: 100,
        headerName: 'Minimum Requirements',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Minimum Requirements"
            params={params}
            keyPrefix={ITEM_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'taxable',
        width: 120,
        headerName: 'Taxable',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Taxable"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{JSON.stringify(params.value)}</span>
        ),
      },
      {
        field: 'action',
        headerName: '',
        width: 55,
        resizable: false,
        sortable: false,
        renderHeader: () => null,
        renderCell: (params) => {
          return <RowButton item={params.row.item}></RowButton>;
        },
      },
    ];
  }, []);

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: GRID_CHECKBOX_COLUMN_ORDER,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <div style={{ height: `calc(100vh - ${topNavBarHeight} - 250px)` }}>
      <DataGridPro
        apiRef={apiRef}
        columnHeaderHeight={56}
        sx={{
          border: 'none',
          padding: '1rem',
          '.MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#ECECEC;',
          },
          '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
          '.MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            whiteSpace: 'normal',
            lineHeight: 1.2,
          },
        }}
        rows={
          results
            ? results.results.map((result) => {
                return {
                  id: result.ItemId,
                  customerItemNumber: result.details.customerItemNumber,
                  vendorItemNumber: result.details.vendorItemNumber,
                  description: result.details.description,
                  taxable: result.details.taxable,
                  customerPrice: result.details.customerPrice,
                  unit: result.details.unit,
                  commissionable: result.details.commissionable,
                  assemblyItem: result.details.assemblyItem,
                  minimumRequirements: result.details.minimumRequirements,
                  item: result,
                };
              })
            : []
        }
        loading={loading}
        columns={columns}
        columnVisibilityModel={persistedPrefs.columnVisibilityModel}
        onColumnVisibilityModelChange={
          persistedPrefs.onColumnVisibilityModelChange
        }
        onColumnWidthChange={persistedPrefs.onColumnWidthChange}
        onColumnOrderChange={persistedPrefs.onColumnOrderChange}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 100,
            },
          },
        }}
        pinnedColumns={{
          right: ['action'],
          left: [GRID_CHECKBOX_SELECTION_FIELD],
        }}
        pageSizeOptions={[1]}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnSorting
        disableColumnFilter
        autoHeight={false}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        slots={{
          loadingOverlay: Loader,
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: { selectedRows: rowSelectionModel },
        }}
      />
    </div>
  );
}
